// Short day/month/time labels for the workflow pages, in the viewer's local time.
// "Sept" (the en-GB short form) is written "Sep" so every month stays three letters.

const parts = (d: Date, opts: Intl.DateTimeFormatOptions) => {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-GB', opts).formatToParts(d)) out[p.type] = p.value === 'Sept' ? 'Sep' : p.value;
  return out;
};

/** Every time in the panel is on the 12-hour clock: "9:14 AM" (seconds: "9:14:05 AM").
 *  en-US whatever the browser language, so it is always upper-case AM / PM. */
export function timeOf(d: Date | string, opts: { seconds?: boolean; timeZone?: string } = {}): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', ...(opts.seconds ? { second: '2-digit' } : {}),
    hour12: true, ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
  });
}

/** A date (in the viewer's language and the given parts) then its 12-hour time:
 *  "Sat, 10 Oct, 4:40 PM". */
export function withTime(d: Date | string, dateOpts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return `${date.toLocaleDateString(undefined, dateOpts)}, ${timeOf(date, { timeZone: dateOpts.timeZone })}`;
}

/** "28 Sep". */
export function dayMonth(iso: string): string {
  const p = parts(new Date(iso), { day: 'numeric', month: 'short' });
  return `${p.day} ${p.month}`;
}

/** "28 Sep, 9:14 AM". */
export function dayMonthTime(iso: string): string {
  const d = new Date(iso);
  const p = parts(d, { day: 'numeric', month: 'short' });
  return `${p.day} ${p.month}, ${timeOf(d)}`;
}

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Timeline "when": "Today, 10:02 AM" or "Sat 10 Oct, 4:40 PM". */
export function eventWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const p = parts(d, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = timeOf(d);
  return sameDay(d, now) ? `Today, ${time}` : `${p.weekday} ${p.day} ${p.month}, ${time}`;
}
